import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contextos/AuthContext';
import { useAvisosSistema } from '../lib/consultas';
import { IconoBuscar, IconoCampana, IconoDocumento, IconoMas, IconoSalir } from './Iconos';
import { InterruptorTema } from './InterruptorTema';
import { PaletaComandos } from './PaletaComandos';

/** Las pestañas del CRM original, con el color de subrayado que cada una tenía. */
const PESTANAS = [
  { ruta: '/', etiqueta: 'Dashboard', acento: 'var(--accent-red)', exacta: true },
  { ruta: '/proyectos', etiqueta: 'Proyectos', acento: 'var(--accent-blue)' },
  { ruta: '/costos', etiqueta: 'Costos', acento: 'var(--accent-yellow)' },
  { ruta: '/cobranza', etiqueta: 'Cobranza', acento: 'var(--accent-green)' },
  { ruta: '/empresa', etiqueta: 'Empresa', acento: 'var(--accent-purple)' },
  { ruta: '/pipeline', etiqueta: 'Pipeline', acento: 'var(--accent-purple)' },
  { ruta: '/calendario', etiqueta: 'Calendario', acento: 'var(--accent-gray)' },
  { ruta: '/organismos', etiqueta: 'Organismos', acento: 'var(--accent-blue)' },
  { ruta: '/competencia', etiqueta: 'Competencia', acento: 'var(--accent-red)' },
  { ruta: '/motor', etiqueta: 'Motor', acento: 'var(--accent-blue)' },
];

export function Layout() {
  const { usuario, cerrarSesion } = useAuth();
  const navegar = useNavigate();
  const [paletaAbierta, setPaletaAbierta] = useState(false);
  const [avisosAbiertos, setAvisosAbiertos] = useState(false);
  const refAvisos = useRef<HTMLDivElement>(null);

  const { data: avisos } = useAvisosSistema();

  // Atajos del CRM original: Ctrl+K para la paleta, Esc para cerrar.
  useEffect(() => {
    const alPresionar = (evento: KeyboardEvent) => {
      if ((evento.ctrlKey || evento.metaKey) && evento.key.toLowerCase() === 'k') {
        evento.preventDefault();
        setPaletaAbierta((abierta) => !abierta);
        return;
      }

      if (evento.key === 'Escape') {
        setPaletaAbierta(false);
        setAvisosAbiertos(false);
        return;
      }

      // La tecla N solo abre el formulario si no se está escribiendo en algún campo.
      const etiqueta = (document.activeElement?.tagName ?? '').toUpperCase();
      const escribiendo = etiqueta === 'INPUT' || etiqueta === 'TEXTAREA' || etiqueta === 'SELECT';
      if (escribiendo) return;

      if (evento.key === 'n' || evento.key === 'N') {
        evento.preventDefault();
        navegar('/licitaciones/nueva');
      }
    };

    window.addEventListener('keydown', alPresionar);
    return () => window.removeEventListener('keydown', alPresionar);
  }, [navegar]);

  // Cerrar el panel de avisos al hacer clic fuera.
  useEffect(() => {
    if (!avisosAbiertos) return;

    const alClicar = (evento: MouseEvent) => {
      if (refAvisos.current && !refAvisos.current.contains(evento.target as Node)) {
        setAvisosAbiertos(false);
      }
    };

    document.addEventListener('mousedown', alClicar);
    return () => document.removeEventListener('mousedown', alClicar);
  }, [avisosAbiertos]);

  return (
    <div className="app-container">
      <header className="header">
        <div className="brand">
          <div className="brand-logo-icon">L</div>
          <span className="brand-name">
            LEXI<span>CORE</span>
          </span>
          <div className="brand-divider" />
          <span className="brand-subtitle">{usuario?.tenantNombre || 'CRM Multi-Panel'}</span>
        </div>

        <div className="header-actions">
          <button
            type="button"
            className="btn-icono"
            onClick={() => setPaletaAbierta(true)}
            title="Buscar y navegar (Ctrl+K)"
            aria-label="Buscar y navegar"
          >
            <IconoBuscar />
          </button>

          <div className="notif-wrap" ref={refAvisos}>
            <button
              type="button"
              className="btn-icono"
              onClick={() => setAvisosAbiertos((abierto) => !abierto)}
              title="Avisos"
              aria-label={`Avisos${avisos?.total ? `: ${avisos.total} pendientes` : ''}`}
            >
              <IconoCampana />
              {avisos && avisos.total > 0 && (
                <span className="notif-badge">{avisos.total > 99 ? '99+' : avisos.total}</span>
              )}
            </button>

            {avisosAbiertos && (
              <div className="notif-panel">
                <div className="notif-header">
                  Avisos {avisos?.criticos ? `· ${avisos.criticos} críticos` : ''}
                </div>
                {avisos && avisos.avisos.length > 0 ? (
                  avisos.avisos.map((aviso, indice) => (
                    <div
                      key={`${aviso.entidadId}-${indice}`}
                      className="notif-item"
                      onClick={() => {
                        setAvisosAbiertos(false);
                        if (aviso.entidad === 'proyecto') navegar(`/proyectos/${aviso.entidadId}`);
                        else if (aviso.entidad === 'factura') navegar('/cobranza');
                        else navegar('/pipeline');
                      }}
                    >
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          marginTop: 6,
                          flexShrink: 0,
                          background: colorNivel(aviso.nivel),
                        }}
                      />
                      <div>
                        <div style={{ lineHeight: 1.45 }}>{aviso.texto}</div>
                        <div
                          style={{
                            fontSize: 9.5,
                            fontWeight: 700,
                            marginTop: 4,
                            textTransform: 'uppercase',
                            color: colorNivel(aviso.nivel),
                          }}
                        >
                          {aviso.nivel}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                    Sin novedades por ahora
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            className="btn-icono"
            onClick={() => navegar('/reporte')}
            title="Reporte ejecutivo"
            aria-label="Abrir el reporte ejecutivo"
          >
            <IconoDocumento />
          </button>

          <InterruptorTema />

          <button
            type="button"
            className="btn-icono"
            onClick={() => void cerrarSesion()}
            title={`Cerrar sesión de ${usuario?.nombre ?? ''}`}
            aria-label="Cerrar sesión"
          >
            <IconoSalir />
          </button>

          <button type="button" className="btn btn-primary" onClick={() => navegar('/licitaciones/nueva')}>
            <IconoMas size={14} />
            Nueva oferta
          </button>
        </div>
      </header>

      <nav className="tabs">
        {PESTANAS.map((pestana) => (
          <NavLink
            key={pestana.ruta}
            to={pestana.ruta}
            end={pestana.exacta}
            className={({ isActive }) => `tab-btn${isActive ? ' activa' : ''}`}
            style={{ '--acento': pestana.acento } as CSSProperties}
          >
            <span className="tab-punto" />
            {pestana.etiqueta}
          </NavLink>
        ))}
      </nav>

      <Outlet />

      <footer style={{ textAlign: 'center', padding: '28px 0 8px', fontSize: 11, color: 'var(--text-muted)', borderTop: '1px solid var(--border-subtle)', marginTop: 32 }}>
        <p>© 2026 LEXICORE LABS SYSTEM LIMITADA</p>
        <p style={{ marginTop: 6 }}>
          Atajos: <kbd>Ctrl</kbd>+<kbd>K</kbd> buscar · <kbd>N</kbd> nueva oferta · <kbd>Esc</kbd> cerrar
        </p>
      </footer>

      {paletaAbierta && <PaletaComandos alCerrar={() => setPaletaAbierta(false)} />}
    </div>
  );
}

function colorNivel(nivel: string): string {
  if (nivel === 'critico') return 'var(--accent-red)';
  if (nivel === 'advertencia') return 'var(--accent-yellow)';
  return 'var(--accent-blue)';
}
